import { defaultHeight } from '@Common/Controls/SimpleGridLight/Editors/EditorProps';
import { safeDestroys } from '@Common/Core/Destroyable';
import {
    Control,
    dropDownColorPicker,
    DropDownColorPicker,
    IDropDownParameters,
    IListItem,
    INamedColorsDefinition,
    IRadioParameters,
    Radio
} from '../../index';
import { GridCell } from '../GridCell';
import { GridColumn } from '../GridColumn';
import { GridRow } from '../GridRow';
import { RowCollection } from '../RowCollection';
import { ICellEditor, IGridColumnEditor } from '../SimpleGridLight';
import { DropDownMode } from './DropDownEditor';

export abstract class GridColumnEditorBase implements IGridColumnEditor {
    public abstract getEditor(row: GridRow, column: GridColumn): ICellEditor;

    public buildEditorForAll(column: GridColumn) {}

    public refreshAll(rows: RowCollection) {}
}

export interface IExternalEditor {
    onChanged(fn: (newValue: any) => void): void;
}

class DropDownColorPickerEditor implements ICellEditor {
    public editor: DropDownColorPicker;
    public editorObject: JQuery;
    public view: JQuery;
    public cell: GridCell;
    public namedColors;

    constructor(cell: GridCell, setting) {
        this.editor = dropDownColorPicker($.extend(setting, { height: defaultHeight }));
        cell.tableCell().addClass('visibleEditor');
        this.namedColors = setting.namedColors || {};
        this.view = $('<div>', {
            class: 'dropDownColorPickerEditor'
        });
        this.cell = cell;
        this.editor.onChange(value => {
            //this.cell.value(ColorHelper.hexToRgba(value));
            this.cell.value(value);
        });
        this.editorObject = this.editor.getControl();
        this.refresh();
        this.switchViewEditor(this.editorObject, this.view);
    }

    private switchViewEditor(control1: JQuery, control2: JQuery) {
        control1.detach();
        control2.appendTo(this.cell.tableCell());
    }

    public beginEdit() {
        this.switchViewEditor(this.view, this.editorObject);
    }

    public endEdit() {
        this.switchViewEditor(this.editorObject, this.view);
    }

    public refresh() {
        let value = this.cell.value();
        this.editor.setValue(value);
        value = this.editor.getColor();
        this.view.css('background-color', value);
    }

    public isInActiveMode() {
        return this.editorObject.hasClass('clicked');
    }

    public destroy(): void {
        safeDestroys(this.editor, this.view);
    }
}

export class GridColorEditor implements IGridColumnEditor {
    constructor(
        private readonly setting: { resetable?: boolean; defaultValue?: string; namedColors?: INamedColorsDefinition } = {
            resetable: false,
            defaultValue: ''
        }
    ) {}

    public getEditor(row: GridRow, column: GridColumn): ICellEditor {
        return new DropDownColorPickerEditor(row.getCell(column), this.setting);
    }

    public buildEditorForAll(column: GridColumn) {}

    public refreshAll(rows: RowCollection) {}
}

export class NullGridColumnEditor implements IGridColumnEditor {
    public getEditor(row: GridRow, column: GridColumn): ICellEditor {
        return null;
    }

    public buildEditorForAll(column: GridColumn) {}

    public refreshAll(rows: RowCollection) {}
}

export class TwoStateCellEditor<TInnerControl extends Control, TSetting> implements ICellEditor {
    constructor(protected cell: GridCell, protected inner: TInnerControl, protected view: JQuery, protected setting: TSetting) {
        this.switchViewEditor(this.inner.getControl(), this.view);
        this.refresh();
    }

    private switchViewEditor(control1: JQuery, control2: JQuery) {
        control1.detach();
        control2.appendTo(this.cell.tableCell());
    }

    public beginEdit() {
        this.switchViewEditor(this.view, this.inner.getControl());
    }

    public endEdit() {
        this.switchViewEditor(this.inner.getControl(), this.view);
    }

    public refresh() {}

    public isInActiveMode() {
        return false;
    }

    public canTab(): boolean {
        return false;
    }

    public destroy(): void {
        safeDestroys(this.inner, this.view);
    }
}

export interface IGridColumnEditorProperties {
    setAll?: boolean;
    setAllValueChanged?: (column: GridColumn, newValue: any) => void;
    onCreateCellEditor?: (cell: GridCell, editor: ICellEditor) => void;
}

export interface IGridDropDownEditorProperties<TId extends string | number = string> extends IGridColumnEditorProperties, IDropDownParameters {
    items?: IListItem<TId>[];
    dropDownMode?: DropDownMode;
    unknownValueText?: string;
    onChange?: IHandler;
}

export class CellCustomEditor<TControl = any> implements ICellEditor {
    public _isInActiveMode = true;
    public _isEditMode = false;
    public currentControl!: TControl;

    constructor(private readonly cell: GridCell, private readonly setting: Required<ICustomEditorSettings>) {
        this.setting.init(this.cell, this);
        this.refresh();
    }

    public isInActiveMode(value?: boolean): boolean {
        if (_.isUndefined(value)) {
            return this._isInActiveMode;
        }
        return (this._isInActiveMode = value);
    }

    public isEditMode(): boolean {
        return this._isEditMode;
    }

    public refresh(): void {
        this.setting.onRefresh(this.cell, this);
    }

    public beginEdit(preview = false): void {
        if (preview && this._isEditMode) {
            return;
        }
        this._isEditMode = true;
        this.setting.onBeginEdit(this.cell, this);
    }

    public endEdit(): void {
        this._isEditMode = false;
        this.setting.onEndEdit(this.cell, this);
    }

    public destroy(): void {
        safeDestroys(this.currentControl);
    }
}

export interface IValueControl {
    adjust(width?: number, height?: number): void;

    value(val?: any): any;

    getControl(): JQuery;

    isInActiveMode(): boolean;
}

export interface ICustomEditorSettings<TControl = any> {
    init?: (cell: GridCell, editor: CellCustomEditor<TControl>) => void;
    onBeginEdit?: (cell: GridCell, editor: CellCustomEditor<TControl>) => void;
    onEndEdit?: (cell: GridCell, editor: CellCustomEditor<TControl>) => void;
    onRefresh?: (cell: GridCell, editor: CellCustomEditor<TControl>) => void;
}

export class ColumnCustomEditor<TControl = any> extends GridColumnEditorBase {
    private readonly setting: Required<ICustomEditorSettings<TControl>>;

    constructor(setting: ICustomEditorSettings<TControl>) {
        super();
        this.setting = _.defaults(setting, {
            init: $.noop,
            onBeginEdit: $.noop,
            onEndEdit: $.noop,
            onRefresh: $.noop
        });
    }

    public getEditor(row: GridRow, column: GridColumn): ICellEditor {
        return new CellCustomEditor(row.getCell(column), this.setting);
    }
}

export interface IRadioEditorSettings {
    blockElement?: boolean;
    setAll?: boolean;
}

class CellRadioEditor implements ICellEditor {
    public cell: GridCell;
    public inner: Radio;

    constructor(cell: GridCell, setting: IRadioParameters) {
        this.inner = new Radio(setting);
        this.cell = cell;
        this.refresh();
        this.inner.onChange(value => {
            this.cell.value(value);
        });
        this.cell.tableCell().empty();
        this.cell.tableCell().css('text-align', 'center');
        this.cell.tableCell().css('padding-bottom', '0');
        this.cell.tableCell().css('padding-top', '0');
        const wrap = $('<div>').css('text-align', 'center').height(24);
        this.inner.getControl().appendTo(wrap);
        wrap.appendTo(this.cell.tableCell());
    }

    public beginEdit() {
        this.cell.tableCell().removeClass('active');
    }

    public endEdit() {}

    public refresh() {
        this.readonly(this.cell.readonly());
        this.inner.value(this.cell.value());
    }

    private readonly(value: boolean) {
        this.inner.enabled(!value);
    }

    public isInActiveMode() {
        return false;
    }

    public destroy(): void {
        safeDestroys(this.inner);
    }
}

export class ColumnRadioEditor extends GridColumnEditorBase {
    private rows: RowCollection;
    private column: GridColumn;

    constructor(private readonly setting: IRadioEditorSettings) {
        super();
        this.setting = $.extend(
            {
                blockElement: false,
                caption: ''
            },
            setting
        );
    }

    public getEditor(row: GridRow, column: GridColumn): ICellEditor {
        this.column = column;
        const param = $.extend(
            {
                getRadioButtonsFromOneGroup: () => this.getAllRadio()
            },
            this.setting
        );
        return new CellRadioEditor(row.getCell(column), param);
    }

    public refreshAll(rows: RowCollection) {
        this.rows = rows;
    }

    public getAllRadio() {
        if (this.rows == null) {
            return [];
        }
        return _.map(this.rows.ToArray(), row => row.getCell(this.column).getEditor<CellRadioEditor>().inner);
    }
}
